import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useAppConfig } from "providers";
import { useSessionFlag } from "hooks/useSessionFlag";
import { CriticalIncidentController } from "../controllers/criticalIncidentController";
import { type ApiError } from "api/error/ApiError";
import { onError } from "api/error/errorHandler";
import { type CIReferral, CIReferralStatus } from "components/CriticalIncident/interfaces";
import { useQueryWithFilter } from "./useQueryWithFilter";
import { useFeatureManager } from "hooks/useFeatureManager";

// Initialize controller with API host
const useController = () => {
    const { REACT_APP_CI_REFERRAL_HOST } = useAppConfig();
    const isMockMode = useSessionFlag<boolean | undefined>('use-mock-server');
    const { isCriticalIncidentFeatureEnabled } = useFeatureManager();
    return new CriticalIncidentController(REACT_APP_CI_REFERRAL_HOST, 'v1', isMockMode, isCriticalIncidentFeatureEnabled());
};

const filterFn = (data: CIReferral): boolean => data.status !== CIReferralStatus.Declined;

// Query hooks
export const useGetCriticalIncidentReferral = (id: string, version = 'v1') => {
    const controller = useController();
    return useQueryWithFilter<CIReferral>({
        queryKey: ['criticalIncident', 'referral', id],
        queryFn: () => controller.getCriticalIncidentReferral(id, version),
        enabled: !!id,
        filterFn,
    });
};

export const useGetCriticalIncidentReferralByExternalId = (externalId: string, version = 'v1') => {
    const controller = useController();
    return useQueryWithFilter<CIReferral>({
        queryKey: ['criticalIncident', 'referral', 'external', externalId],
        queryFn: () => controller.getCriticalIncidentReferralByExternalId(externalId, version),
        enabled: !!externalId,
        filterFn,
    });
};

export const useGetCriticalIncidentReferrals = (version = 'v1', enabled = true) => {
    const controller = useController();
    return useQueryWithFilter<CIReferral[]>({
        queryKey: ['criticalIncident', 'referrals'],
        queryFn: () => controller.getCriticalIncidentReferrals(version),
        enabled,
        filterFn,
    });
};

// Mutation hooks
export const useAcceptCriticalIncidentReferral = (options?: UseMutationOptions<CIReferral, ApiError, string>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: (id: string) => controller.acceptCriticalIncidentReferral(id, version),
        mutationKey: ['criticalIncident', 'referral', 'accept'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};

export const useDeclineCriticalIncidentReferral = (options?: UseMutationOptions<CIReferral, ApiError, { id: string, reason: string }>, version = 'v1') => {
    const controller = useController();
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) => 
            controller.declineCriticalIncidentReferral(id, reason, version),
        mutationKey: ['criticalIncident', 'referral', 'decline'],
        ...options,
        onError: typeof options?.onError === 'function' ? options.onError : onError
    });
};
